import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import '../../css/RichTextInput.css';

// Hacer que los enlaces sin protocolo (ej. www.google.es) se guarden con https://
const Link = Quill.import('formats/link');
class CustomLink extends Link {
  static sanitize(url) {
    const value = super.sanitize(url);
    if (!value) return value;
    const hasProtocol = (Link.PROTOCOL_WHITELIST || ['http', 'https', 'mailto', 'tel']).some(
      (p) => value.toLowerCase().startsWith(p + ':')
    );
    if (hasProtocol) return value;
    return `https://${value}`;
  }
}
Quill.register(CustomLink, true);

/**
 * Editor de texto enriquecido con barra de formato.
 * Incluye: negrita, cursiva, subrayado, tachado, tipo de letra, tamaño, color.
 * El pegado desde el portapapeles preserva el formato (HTML).
 */
const RichTextInput = ({
  id,
  ariaLabelledBy,
  initialContent = '',
  onChange,
  className = '',
  style = {},
  placeholder = 'Escribe o pega aquí...',
  editorKey,
}) => {
  const [content, setContent] = useState(initialContent);

  // Solo sincronizar cuando cambia editorKey (= se abre un editor distinto)
  useEffect(() => {
    setContent(initialContent);
  }, [editorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ font: ['sans-serif', 'serif', 'monospace'] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'clean'],
      ],
      clipboard: {
        matchVisual: true,
      },
    }),
    []
  );

  const formats = [
    'font',
    'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'link',
  ];

  const handleChange = useCallback(
    (value) => {
      setContent(value);
      if (onChange) onChange(value);
    },
    [onChange]
  );

  return (
    <div
      id={id}
      className={`rich-text-input-wrapper ${className}`.trim()}
      style={{ direction: 'ltr', ...style }}
      aria-labelledby={ariaLabelledBy || undefined}
    >
      <ReactQuill
        key={editorKey ?? id ?? 'quill'}
        theme="snow"
        value={content}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ minHeight: '120px' }}
      />
    </div>
  );
};

export default RichTextInput;
